import type { ReactNode } from "react"

import { Button, Input } from "@coval/ui"
import { Minus, Plus } from "lucide-react"

export interface KeyValueEditorRow {
  id: string | number
  key: string
  value: string
}

interface KeyValueRowsEditorProps<Id extends string | number> {
  rows: Array<KeyValueEditorRow & { id: Id }>
  onChange: (
    id: Id,
    patch: Partial<Pick<KeyValueEditorRow, "key" | "value">>,
  ) => void
  onRemove: (id: Id) => void
  onAdd: () => void
  keyLabel?: string
  valueLabel?: string
  addLabel?: string
  disabled?: boolean
  canRemove?: (row: KeyValueEditorRow & { id: Id }) => boolean
  getKeyError?: (row: KeyValueEditorRow & { id: Id }) => string | undefined
  getKeyLabel?: (row: KeyValueEditorRow & { id: Id }) => string
  getRemoveLabel?: (row: KeyValueEditorRow & { id: Id }) => string
  renderMiddle?: (row: KeyValueEditorRow & { id: Id }) => ReactNode
  renderValue?: (row: KeyValueEditorRow & { id: Id }) => ReactNode
}

/**
 * Shared, controlled key-value row editor. Callers own conversion between the
 * draft strings shown here and the record shape they persist.
 */
export function KeyValueRowsEditor<Id extends string | number>({
  rows,
  onChange,
  onRemove,
  onAdd,
  keyLabel = "Key",
  valueLabel = "Value",
  addLabel = "Add field",
  disabled = false,
  canRemove,
  getKeyError,
  getKeyLabel,
  getRemoveLabel,
  renderMiddle,
  renderValue,
}: KeyValueRowsEditorProps<Id>) {
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const keyError = getKeyError?.(row)
        return (
          <div key={row.id} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Input
                aria-label={getKeyLabel?.(row) ?? keyLabel}
                aria-invalid={keyError ? true : undefined}
                placeholder={keyLabel}
                value={row.key}
                onChange={(event) =>
                  onChange(row.id, { key: event.target.value })
                }
                error={Boolean(keyError)}
                disabled={disabled}
              />
              {keyError && (
                <p role="alert" className="mt-1 text-xs text-coval-danger-text">
                  {keyError}
                </p>
              )}
            </div>
            {renderMiddle?.(row)}
            {renderValue?.(row) ?? (
              <Input
                aria-label={valueLabel}
                placeholder={valueLabel}
                value={row.value}
                onChange={(event) =>
                  onChange(row.id, { value: event.target.value })
                }
                className="min-w-0 flex-1"
                disabled={disabled}
              />
            )}
            {(!canRemove || canRemove(row)) && (
              <Button
                type="button"
                icon
                variant="ghost"
                tooltip={
                  getRemoveLabel?.(row) ?? `Remove ${keyLabel.toLowerCase()}`
                }
                onClick={() => onRemove(row.id)}
                disabled={disabled}
              >
                <Minus className="size-4" />
              </Button>
            )}
          </div>
        )
      })}
      <Button
        type="button"
        variant="ghost"
        onClick={onAdd}
        className="h-8 px-2 text-xs"
        disabled={disabled}
      >
        <Plus className="mr-1.5 size-3.5" />
        {addLabel}
      </Button>
    </div>
  )
}
